import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  ResetFormDataAction,
  SetFormActionWithId,
} from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { SetPageContext } from '@app/finxone-web-frontend/app/lib/actions/page-context.action';
import { CountdownService } from '@app/finxone-web-frontend/app/lib/services/countdown-service/countdown-service.service';
import { ExchangeService } from '@app/finxone-web-frontend/app/lib/services/exchange-service/exchange-service.service';
import { AccountState } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  ExchangeQuotePayload,
  FieldAttribute,
  UiZoneWidgetAttributeConfig,
  isMultiCurrencyAccountInfoType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { handleRedirection } from '../cta-redirection.utils';

export function reQuote(injector: Injector, widgetProperties: BaseWidgetProperties) {
  const store = injector.get(Store);
  const formStateData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.CURRENCY_EXCHANGE),
  );
  const exchangeService = injector.get(ExchangeService);
  const countdownService = injector.get(CountdownService);
  const quoteExpiration = widgetProperties['field_attributes']?.find(
    (field: FieldAttribute) => field.name === 'quoteExpiration',
  ).value;

  if (
    formStateData?.formData &&
    formStateData?.formData['buyAccountId'] &&
    formStateData?.formData['sellAccountId'] &&
    formStateData?.formData['amount']
  ) {
    const exchangeQuotePayload: ExchangeQuotePayload = formStateData.formData as ExchangeQuotePayload;
    const lastQuoteId = exchangeService.lastQuoteResponseData$.getValue()?.quoteId;
    if (exchangeQuotePayload && lastQuoteId) {
      exchangeService.postRefreshQuote(lastQuoteId, exchangeQuotePayload).subscribe(() => {
        countdownService.startCountdown(quoteExpiration);
      });
    }
  }
}

export function acceptQuote(
  injector: Injector,
  alertHandlerService: AlertHandlerService,
  router: Router,
  attri: UiZoneWidgetAttributeConfig,
) {
  const store = injector.get(Store);
  const formStateData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.CURRENCY_EXCHANGE),
  );
  const exchangeService = injector.get(ExchangeService);
  const exchangeQuotePayload: ExchangeQuotePayload = formStateData?.formData as ExchangeQuotePayload;
  const lastQuoteId = exchangeService.lastQuoteResponseData$.getValue()?.quoteId;
  const accountsData = store.selectSnapshot(AccountState.getAccounts);
  const actionButtonNavigationConfigurations = attri?.widgetProperties?.actionNavigationConfigurations;

  if (lastQuoteId) {
    const sellAccountId = exchangeQuotePayload.sellAccountId;
    const account = accountsData.accounts.find((account) => account.accountId === sellAccountId);
    if (!account) {
      console.error('Account with sellAccountId not found');
      return;
    }

    let accountBalance: number;
    if (isMultiCurrencyAccountInfoType(account)) {
      const sellCurrency = exchangeQuotePayload.sellCurrency;
      const balEntry = account.availableBalance.find((b) => b.currency === sellCurrency);
      accountBalance = Number(balEntry?.amount ?? 0);
    } else {
      accountBalance = Number(account.balance);
    }
    const lastExchangeQuoteResponseData = exchangeService.lastQuoteResponseData$.getValue();
    const isBalanceAvailable = lastExchangeQuoteResponseData?.debitAmount
      ? accountBalance >= lastExchangeQuoteResponseData?.debitAmount
      : false;
    if (!isBalanceAvailable) {
      alertHandlerService.showAlertFn('error', 'Enter a lower amount to continue.', 'Insufficient balance.');
      return;
    }

    if (lastExchangeQuoteResponseData?.quoteId) {
      exchangeService.postAcceptQuote(lastExchangeQuoteResponseData?.quoteId).subscribe(
        (result) => {
          store.dispatch(new ResetFormDataAction());
          store.dispatch(
            new SetPageContext({
              pageContextData: { conversionRef: result.transactionNumber },
            }),
          );
          store.dispatch(
            new SetFormActionWithId(
              {
                type: '',
                formData: result,
              },
              FormActionTypeEnum.EXCHANGE_QUOTE_SUMMARY,
            ),
          );
          if (actionButtonNavigationConfigurations?.success) {
            handleRedirection(attri.widgetProperties, injector, ActionButtonOutcomesType.SUCCESS, true);
          } else {
            router.navigate(['/zones/payments/conversion-successful']);
          }
        },
        (error) => {
          console.error(error);
          store.dispatch(
            new SetFormActionWithId(
              {
                type: '',
                formData: exchangeQuotePayload,
              },
              FormActionTypeEnum.EXCHANGE_QUOTE_SUMMARY,
            ),
          );
          if (actionButtonNavigationConfigurations?.failure) {
            handleRedirection(attri.widgetProperties, injector, ActionButtonOutcomesType.FAILURE, true);
          } else {
            router.navigate(['/zones/payments/conversion-failed']);
          }
        },
      );
    }
  }
}
